import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShieldCheck,
  ShieldAlert,
  KeyRound,
  Smartphone,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react';
import { api } from '@/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';

export const Route = createFileRoute('/account/2fa')({ component: TwoFactorPage });

interface TwoFactorStatusDto {
  isTwoFactorEnabled: boolean;
  hasAuthenticator: boolean;
  recoveryCodesLeft: number;
  isMachineRemembered: boolean;
}
interface AuthenticatorSetupDto {
  sharedKey: string;
  authenticatorUri: string;
}
interface EnableResponse {
  recoveryCodes: string[];
}
interface RecoveryCodesResponse {
  recoveryCodes: string[];
}

function TwoFactorPage() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['account', '2fa', 'status'],
    queryFn: () => api<TwoFactorStatusDto>('/account/2fa'),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['account', '2fa', 'status'] });

  const disable = useMutation({
    mutationFn: () => api('/account/2fa/disable', { method: 'POST' }),
    onSuccess: invalidate,
  });
  const reset = useMutation({
    mutationFn: () => api('/account/2fa/reset-authenticator', { method: 'POST' }),
    onSuccess: invalidate,
  });
  const forget = useMutation({
    mutationFn: () => api('/account/2fa/forget-machine', { method: 'POST' }),
    onSuccess: invalidate,
  });

  if (status.isLoading) {
    return <p className="text-[14px] text-(--color-ink-muted)">Loading two-factor status…</p>;
  }
  const s = status.data;
  const enabled = !!s?.isTwoFactorEnabled;

  return (
    <div className="space-y-6">
      <div>
        <div className="section-ref">Two-factor authentication</div>
        <h2 className="mt-1.5 font-serif text-2xl font-medium tracking-tight">
          Add a second sign-in step
        </h2>
        <div className="mt-1 gov-rule" aria-hidden />
        <p className="mt-4 max-w-prose text-[14px] leading-relaxed text-(--color-ink-2)">
          Pair an authenticator app with this account. Even if your password is compromised, no
          one can sign in without the rolling code from your phone.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-4 p-7">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div
                className={`flex size-10 shrink-0 items-center justify-center rounded-(--radius-sm) ${
                  enabled
                    ? 'bg-(--color-forest-soft) text-(--color-forest)'
                    : 'bg-(--color-ochre-soft) text-(--color-ochre)'
                }`}
              >
                {enabled ? (
                  <ShieldCheck className="size-5" strokeWidth={1.7} />
                ) : (
                  <ShieldAlert className="size-5" strokeWidth={1.7} />
                )}
              </div>
              <div>
                <h3 className="font-serif text-xl font-medium text-(--color-ink)">
                  Two-factor is {enabled ? 'enabled' : 'not enabled'}
                </h3>
                <p className="mt-1 text-[13px] text-(--color-ink-2)">
                  {enabled
                    ? 'You will be asked for a 6-digit code from your authenticator app on each new sign-in.'
                    : 'Your account is protected by your password alone. Enable 2FA below.'}
                </p>
              </div>
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-3 border-t border-(--color-rule) pt-4 sm:grid-cols-2">
            <Row label="Authenticator app" value={s?.hasAuthenticator ? 'Configured' : 'Not configured'} />
            <Row
              label="Recovery codes left"
              value={String(s?.recoveryCodesLeft ?? 0)}
              warning={(s?.recoveryCodesLeft ?? 0) < 3}
            />
            <Row label="This device remembered" value={s?.isMachineRemembered ? 'Yes' : 'No'} />
          </dl>

          {(enabled || s?.isMachineRemembered) && (
            <div className="flex flex-wrap gap-2 border-t border-(--color-rule) pt-4">
              {enabled && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => disable.mutate()}
                  disabled={disable.isPending}
                >
                  Disable 2FA
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => reset.mutate()} disabled={reset.isPending}>
                <RefreshCw className="size-3.5" />
                Reset authenticator
              </Button>
              {s?.isMachineRemembered && (
                <Button size="sm" variant="ghost" onClick={() => forget.mutate()} disabled={forget.isPending}>
                  Forget this device
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {!enabled && <SetupPanel onEnabled={invalidate} />}
      {enabled && <RecoveryCodesPanel />}
    </div>
  );
}

function Row({
  label,
  value,
  warning,
}: {
  label: string;
  value: string;
  warning?: boolean;
}) {
  return (
    <div>
      <dt className="section-ref">{label}</dt>
      <dd
        className={`mt-1 text-[14px] font-medium ${
          warning ? 'text-(--color-maroon)' : 'text-(--color-ink)'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function SetupPanel({ onEnabled }: { onEnabled: () => void }) {
  const setup = useQuery({
    queryKey: ['account', '2fa', 'setup'],
    queryFn: () => api<AuthenticatorSetupDto>('/account/2fa/setup'),
  });
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);

  const enable = useMutation({
    mutationFn: () => api<EnableResponse>('/account/2fa/enable', { method: 'POST', json: { code } }),
    onSuccess: (resp) => {
      setCodes(resp?.recoveryCodes ?? []);
      onEnabled();
    },
  });

  return (
    <Card>
      <CardContent className="space-y-5 p-7">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-(--radius-sm) bg-(--color-navy-soft) text-(--color-navy)">
            <Smartphone className="size-5" strokeWidth={1.7} />
          </div>
          <div>
            <h3 className="font-serif text-xl font-medium text-(--color-ink)">
              Set up authenticator
            </h3>
            <p className="mt-1 text-[13px] text-(--color-ink-2)">
              Use any TOTP app — 1Password, Authy, Google Authenticator, Microsoft Authenticator,
              Bitwarden — to scan or enter the secret below.
            </p>
          </div>
        </div>

        {setup.isLoading && (
          <p className="text-[13px] text-(--color-ink-muted)">Generating secret…</p>
        )}
        {setup.data && (
          <div className="space-y-4">
            <div className="rounded-(--radius-sm) border border-(--color-rule) bg-(--color-paper-2) p-4">
              <div className="section-ref">Shared key</div>
              <code className="mt-2 block break-all font-mono text-[13px] tracking-[0.1em] text-(--color-ink)">
                {setup.data.sharedKey}
              </code>
              <div className="section-ref mt-4">Authenticator URI</div>
              <code className="mt-1 block break-all font-mono text-[11px] text-(--color-ink-muted)">
                {setup.data.authenticatorUri}
              </code>
            </div>

            <div className="space-y-2">
              <Label htmlFor="totp" required>
                Enter the 6-digit code from your app
              </Label>
              <Input
                id="totp"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputMode="numeric"
                maxLength={6}
                autoComplete="one-time-code"
                className="font-mono tracking-[0.4em] text-center text-lg"
              />
            </div>

            {enable.isError && (
              <p className="flex items-start gap-2.5 rounded-(--radius-sm) border-l-[3px] border-(--color-maroon) bg-(--color-maroon-soft) px-3 py-2.5 text-[13px] font-medium text-(--color-maroon)">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                Invalid code — try again. Codes refresh every 30 seconds.
              </p>
            )}

            <div className="flex justify-end">
              <Button onClick={() => enable.mutate()} disabled={enable.isPending || code.length !== 6}>
                <ShieldCheck className="size-4" />
                {enable.isPending ? 'Verifying…' : 'Enable 2FA'}
              </Button>
            </div>

            {codes && (
              <div className="rounded-(--radius-sm) border-[1.5px] border-(--color-ochre) bg-(--color-ochre-soft)/40 p-5">
                <div className="flex items-start gap-2.5">
                  <KeyRound className="mt-0.5 size-4 shrink-0 text-(--color-ochre)" />
                  <div>
                    <div className="font-serif text-base font-medium text-(--color-ink)">
                      Recovery codes — save these immediately
                    </div>
                    <p className="mt-1 text-[12px] text-(--color-ink-2)">
                      Each code can be used once if you lose access to your authenticator. Store
                      them in a password manager or print them on paper.
                    </p>
                  </div>
                </div>
                <ul className="mt-4 grid grid-cols-2 gap-2 font-mono text-[13px] tabular-nums">
                  {codes.map((c) => (
                    <li
                      key={c}
                      className="rounded-(--radius-sm) border border-(--color-rule) bg-(--color-card) px-3 py-2 text-center text-(--color-ink)"
                    >
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecoveryCodesPanel() {
  const [codes, setCodes] = useState<string[] | null>(null);
  const generate = useMutation({
    mutationFn: () => api<RecoveryCodesResponse>('/account/2fa/generate-recovery-codes', { method: 'POST' }),
    onSuccess: (r) => setCodes(r?.recoveryCodes ?? []),
  });

  return (
    <Card>
      <CardContent className="space-y-4 p-7">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-(--radius-sm) bg-(--color-navy-soft) text-(--color-navy)">
            <KeyRound className="size-5" strokeWidth={1.7} />
          </div>
          <div>
            <h3 className="font-serif text-xl font-medium text-(--color-ink)">Recovery codes</h3>
            <p className="mt-1 text-[13px] text-(--color-ink-2)">
              Generating new codes invalidates the previous batch. Use this if you've lost track
              of your codes or used too many.
            </p>
          </div>
        </div>
        <div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
          >
            <RefreshCw className="size-3.5" />
            {generate.isPending ? 'Generating…' : 'Generate new codes'}
          </Button>
        </div>
        {codes && (
          <div className="space-y-2">
            <StatusPill tone="success" icon={<CheckCircle2 className="size-3" />}>
              New codes generated — save them now
            </StatusPill>
            <ul className="grid grid-cols-2 gap-2 rounded-(--radius-sm) border border-(--color-rule) bg-(--color-paper-2) p-4 font-mono text-[13px] tabular-nums">
              {codes.map((c) => (
                <li
                  key={c}
                  className="rounded-(--radius-sm) border border-(--color-rule) bg-(--color-card) px-3 py-2 text-center text-(--color-ink)"
                >
                  {c}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
